import { lazy, Suspense } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Link, Outlet } from '@tanstack/react-router';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { authApi } from '@/lib/api/auth';
import { refreshAccessToken } from '@/lib/api-client';
import { getAuthState, markAnonymous, setProfile } from '@/lib/auth-store';

export interface RouterContext {
  queryClient: QueryClient;
}

/** Runs exactly once per page load, no matter how many routes ask for it. */
let bootstrap: Promise<void> | null = null;

/**
 * Attempts a single silent refresh so a returning user with a live `sf_rt`
 * cookie lands back in the app instead of on the sign-in screen.
 */
export function ensureSessionRestored(): Promise<void> {
  bootstrap ??= (async () => {
    if (getAuthState().status !== 'unknown') return;

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      markAnonymous();
      return;
    }

    try {
      const me = await authApi.me();
      setProfile(me.user, me.organisations);
    } catch {
      markAnonymous();
    }
  })();

  return bootstrap;
}

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((mod) => ({
        default: mod.TanStackRouterDevtools,
      })),
    )
  : () => null;

function RootComponent() {
  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <Toaster />
      {import.meta.env.DEV ? (
        <Suspense fallback={null}>
          <RouterDevtools position="bottom-right" />
        </Suspense>
      ) : null}
    </TooltipProvider>
  );
}

function RootNotFound() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-2xl items-center px-6">
      <EmptyState
        kicker="404"
        title="That page does not exist"
        description="The link may be out of date, or the resource may have been moved to a different organisation."
        action={
          <Button asChild variant="secondary">
            <Link to="/app">Back to the dashboard</Link>
          </Button>
        }
        className="w-full"
      />
    </main>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: () => ensureSessionRestored(),
  component: RootComponent,
  notFoundComponent: RootNotFound,
});
